import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { fetchContentBySlug } from 'api/contentfulContent';

const MAX_DEEPNESS = 10;
export default (WrappedComponent) =>
  class WithContentfulDataHOC extends PureComponent {
    static propTypes = {
      contentType: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    };

    state = {
      content: null,
      isLoading: true,
      isError: false,
    };

    componentDidMount() {
      const { contentType, slug } = this.props;
      this.fetchContent(contentType, slug);
    }

    componentDidUpdate(prevProps) {
      // Only update if slug or contentType changes
      if (this.props.slug !== prevProps.slug || this.props.contentType !== prevProps.contentType) {
        this.fetchContent(this.props.contentType, this.props.slug);
      }
    }

    processContent = (content, deepness = 0) => {
      if (deepness >= MAX_DEEPNESS || !content) {
        return content;
      }

      const { sys, fields, file } = content;

      if (Array.isArray(content)) {
        return content.map((value) => this.processContent(value, deepness + 1));
      }

      if (fields && content.sys) {
        return {
          id: sys.id || uuid(),
          contentTypeId: sys.contentType && sys.contentType.sys.id,
          ...this.processContent(fields, deepness + 1),
        };
      }

      if (fields) {
        return this.processContent(fields, deepness + 1);
      }

      if (file && file.url) {
        return this.processContent(file, deepness + 1);
      }

      if (content === Object(content)) {
        return Object.keys(content).reduce((result, key) => {
          result[key] = this.processContent(content[key], deepness + 1);
          return result;
        }, {});
      }

      return content;
    };

    fetchContent = async (contentType, slug) => {
      this.setState({
        isLoading: true,
        isError: false,
        content: null,
      });
      const content = await fetchContentBySlug(contentType, slug);
      if (content) {
        const processedContent = this.processContent(content);
        this.setState({
          isLoading: false,
          isError: false,
          ...processedContent,
        });
      } else {
        this.setState({
          isLoading: false,
          isError: true,
        });
      }
    };

    render() {
      const { content, ...state } = this.state;
      return <WrappedComponent {...state} {...content} {...this.props} />;
    }
  };
